import styled from "@emotion/styled";
import FloatingPanel from "@imminently/decisively-ui/FloatingPanel";
import Icon from "@imminently/decisively-ui/Icon";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import Popover, { type PopoverProps } from "@imminently/decisively-ui/Popover";
import TextInput from "@imminently/decisively-ui/TextInput";
import { Command } from "cmdk";
import uniqBy from "lodash/uniqBy";
import { type PropsWithChildren, type ReactNode, useMemo, useState } from "react";

import { type AuthUser, userService } from "@/app/providers/services";
import { userToAssignee } from "@/common/util";
import { scrollable } from "@/common/scrollbar";
import { type UseSearchProps, useSearch } from "@/components/Search";
import { useScrollable } from "@/hooks/use-scrollable";
import Avatar from "@imminently/decisively-ui/Avatar";
import Button from "@imminently/decisively-ui/Button";

import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import { Typography } from "@mui/material";
import type { AssigneesData } from "@packages/commons";
import "../styles.scss";

const mapUser = (user: AssigneesData) => ({
  ...user,
  value: user.identity_id,
  label: user.name,
});

const getUserKey = (user: any) => user.identity_id ?? user.id ?? user.email ?? user.label;

const UserItem = ({ user, endIcon = null }: { user: any; endIcon?: ReactNode }) => {
  return (
    <div className="UserItem">
      <Avatar
        size="sm"
        alt={user.first_name}
        name={user.label}
      />
      <Typography variant="body1">{user.label}</Typography>
      {endIcon}
    </div>
  );
};

/** @deprecated Use UserCombobox instead */
export const UserSelect = ({ value, onChange, children }) => {
  const [search, setSearch] = useState("");
  const { data, isLoading } = userService.useGetList({ meta: { first_name: `*${search}*` } });

  // const searchUsers = useCallback(debounce(name => {
  //   // console.log('search users debounced, searching...', name);
  //   dispatch(getUsers(name));
  // }, 300), [dispatch]);

  const onInputChange = (v) => {
    if (v !== search) {
      // console.log('search input changed', v);
      setSearch(v);
      // searchUsers(v);
    }
  };

  const addUser = (user) => {
    // debugger;
    onChange([...value, user]);
  };

  const removeUser = (user) => {
    onChange(value.filter((v) => v.id !== user.id));
  };

  const users: AssigneesData[] = data?.data.map((u) => userToAssignee(u as AuthUser)) ?? [];
  const selected = value.map(mapUser); // adding the value and label

  const options =
    users &&
    // @ts-ignore
    uniqBy(users, (u) => u.identity_id)
      .map(mapUser)
      .filter((u) => !selected.find((s) => s.id === u.id));

  console.log("user options", users, options);
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="SelectButton"
        >
          {children}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className="DropdownMenuContent"
        align="start"
      >
        <div className="SearchContainer">
          <Icon name="search" />
          <TextInput
            autoFocus
            className="SearchInput"
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => onInputChange(e.target.value)}
            fullWidth
          />
        </div>
        <DropdownMenu.Separator className="DropdownMenuSeparator" />
        {selected && selected.length > 0 ? (
          <>
            <DropdownMenu.Label className="DropdownMenuLabel">Assignees</DropdownMenu.Label>
            <DropdownMenu.Group className="DropdownMenuGroup">
              {selected.map((item, index) => (
                <DropdownMenu.Item
                  key={index}
                  className="DropdownMenuItem"
                  onSelect={() => removeUser(item)}
                >
                  <UserItem user={item} />
                  <div className="DropdownMenuItemIndicator">
                    <Icon name="clear" />
                  </div>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Group>
          </>
        ) : null}
        {isLoading ? (
          <LoadingSpinner size="sm" />
        ) : (
          <>
            <DropdownMenu.Label className="DropdownMenuLabel">People</DropdownMenu.Label>
            <DropdownMenu.Group className="DropdownMenuGroup">
              {options.map((item, index) => (
                <DropdownMenu.Item
                  key={index}
                  className="DropdownMenuItem"
                  onClick={() => addUser(item)}
                >
                  <UserItem user={item} />
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Group>
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

const PopperContent = styled(FloatingPanel)`
  min-width: 300px;
  max-width: 300px;
  padding: 0.5rem;
`;

const CommandMenu = styled(Command)`
  outline: none;

  [cmdk-input] {
    font-family: inherit;
    border: 1px solid var(--secondary-dark);
    padding: 0.5rem 1rem;
    width: 100%;
    border-radius: 0.5rem;
    font-size: 1rem;
    outline: none;

    &:hover, &:focus, &:active, &:focus-within {
      border-color: var(--foreground);
    }
  }

  [cmdk-group-heading] {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: inherit;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.75rem;
    padding: 0 1rem;
    text-transform: uppercase;
  }

  [cmdk-list] {
    max-height: 300px;
    margin: 0 -0.5rem;
  }

  [cmdk-item] {
    cursor: pointer;
    &[data-selected="true"] {
      background-color: var(--secondary-hover);
    }
  }

  [cmdk-loading] {
    text-align: center;
    padding: 1rem;
  }

  [cmdk-empty] {
    text-align: center;
    padding: 1rem;
  }
`;

export type UserComboboxProps = {
  value: any[];
  onChange: (value: any[]) => void;
  searchProps?: UseSearchProps;
  closeOnSelect?: boolean;
  placement?: PopoverProps["placement"];
  popover?: { align?: "start" | "center" | "end" };
} & PropsWithChildren;

export const UserCombobox = ({
  value,
  onChange,
  searchProps,
  closeOnSelect,
  placement: placementProp,
  popover,
  children,
}: UserComboboxProps) => {
  const { filter: sFilter, ...search } = useSearch(searchProps);
  const { data, total, query, loadMore } = userService.useInfiniteResources({
    perPage: 10,
    filter: {
      email: sFilter.length > 0 ? sFilter : undefined,
    },
  });

  const canLoad = !query.isError && !query.isFetching && (query.hasNextPage ?? false);
  const scrollLoad = useScrollable(loadMore, canLoad);
  const [open, setOpen] = useState(false);
  const placement = placementProp ?? (popover?.align === "end" ? "bottom-end" : "bottom-start");

  const onVisibleChange = (v: boolean) => {
    setOpen(v);
    // clear search on close
    if (!v) {
      search.setValue("");
    }
  };

  const addUser = (user) => {
    onChange([...value, user]);
  };

  const removeUser = (user) => {
    onChange(value.filter((v) => v.id !== user.id));
  };

  // make sure to map into AssigneeData
  const users: AssigneesData[] = useMemo(() => data.map((u) => userToAssignee(u as AuthUser)), [data]);
  const selected = useMemo(() => value.map(mapUser), [value]); // adding the value and label

  const options = useMemo(() => {
    const selectedIds = new Set(selected.map((user) => user.identity_id));
    return uniqBy(users, (user) => user.identity_id)
      .map(mapUser)
      .filter((user) => !selectedIds.has(user.identity_id));
  }, [selected, users]);

  return (
    <Popover
      visible={open}
      onVisibleChange={onVisibleChange}
      mode="click"
      interactable={true}
      placement={placement}
      autoFocus={true}
    >
      <Popover.Reference asChild>{children}</Popover.Reference>
      <Popover.Content asChild={true}>
        <PopperContent
          placement={placement}
          open={open}
        >
          <CommandMenu shouldFilter={false}>
            <Command.Input
              autoFocus
              placeholder={search.placeholder}
              value={search.value}
              onValueChange={search.setValue}
            />
            <Command.List {...scrollLoad} className="scrollable">
              {query.isLoading ? (
                <Command.Loading>
                  <LoadingSpinner size="sm" />
                </Command.Loading>
              ) : total === 0 ? (
                <Command.Empty>No results found.</Command.Empty>
              ) : (
                <>
                  {selected && selected.length > 0 ? (
                    <>
                      <Command.Group heading="Assignees">
                        {selected.map((item) => (
                          <Command.Item
                            key={getUserKey(item)}
                            value={item.identity_id}
                          >
                            <UserItem
                              user={item}
                              endIcon={
                                <Button
                                  icon
                                  size="sm"
                                  variant="ghost"
                                  label={`Remove ${item.label}`}
                                  onMouseDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                  }}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    removeUser(item);
                                  }}
                                >
                                  <Icon name="clear" />
                                </Button>
                              }
                            />
                          </Command.Item>
                        ))}
                      </Command.Group>
                    </>
                  ) : null}
                  <Command.Group heading="People">
                    {options.map((item) => (
                      <Command.Item
                        key={getUserKey(item)}
                        value={item.identity_id}
                        onSelect={() => {
                          addUser(item);
                          if (closeOnSelect) {
                            onVisibleChange(false);
                          }
                        }}
                      >
                        <UserItem user={item} />
                      </Command.Item>
                    ))}
                  </Command.Group>
                  {query.isFetchingNextPage ? (
                    <Command.Loading>
                      <LoadingSpinner size="sm" />
                    </Command.Loading>
                  ) : null}
                </>
              )}
            </Command.List>
          </CommandMenu>
        </PopperContent>
      </Popover.Content>
    </Popover>
  );
};
